import { useEffect, useState } from 'react';
import { CheckCircle2, AlertTriangle, RefreshCw, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Compact admin banner that surfaces the build/publish status so the
 * operator can confirm at a glance whether the version they are editing
 * matches what is live on the published custom domain.
 *
 * - Local build info is injected at build time via `__BUILD_TIME__` /
 *   `__BUILD_ID__` (see vite.config.ts).
 * - Live build info is fetched from the apex domain (`yohq.org`), which
 *   emits `build-info.json` alongside `index.html`. We normalize the
 *   host by stripping a leading `www.` to avoid 503s on the www subdomain.
 * - Environment is derived from `window.location.host` so the banner
 *   clearly labels Preview vs Published vs Custom Domain vs Local Dev.
 */

const DEFAULT_LIVE_ORIGIN = 'https://yohq.org';

function normalizeLiveHost(host: string): string {
  return host.replace(/^www\./, '');
}

function getLiveOrigin(host: string): string {
  if (!host) return DEFAULT_LIVE_ORIGIN;
  return `https://${normalizeLiveHost(host)}`;
}

type LiveStatus =
  | { state: 'loading' }
  | { state: 'match'; liveBuildId: string; liveBuildTime: string }
  | { state: 'mismatch'; liveBuildId: string; liveBuildTime: string }
  | { state: 'error'; message: string };

function detectEnvironment(host: string): { label: string; tone: 'live' | 'preview' | 'dev' } {
  if (host === 'yohq.org' || host === 'www.yohq.org') {
    return { label: 'Custom Domain (live)', tone: 'live' };
  }
  if (host.endsWith('.lovable.app') && !host.startsWith('id-preview--')) {
    return { label: 'Published Preview', tone: 'live' };
  }
  if (host.startsWith('id-preview--')) {
    return { label: 'Lovable Editor Preview', tone: 'preview' };
  }
  if (host.endsWith('.lovableproject.com')) {
    return { label: 'Lovable Sandbox', tone: 'preview' };
  }
  return { label: 'Local Dev', tone: 'dev' };
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return iso;
  const diff = Date.now() - then;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} h ago`;
  return `${Math.floor(diff / 86_400_000)} d ago`;
}

const AdminBuildStatusBanner = () => {
  const localBuildId = __BUILD_ID__;
  const localBuildTime = __BUILD_TIME__;
  const [host, setHost] = useState('');
  const [live, setLive] = useState<LiveStatus>({ state: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [dismissedRefresh, setDismissedRefresh] = useState(false);
  const liveOrigin = getLiveOrigin(host);

  useEffect(() => {
    setHost(typeof window !== 'undefined' ? window.location.host : '');
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLive({ state: 'loading' });
    fetch(`${liveOrigin}/build-info.json?_=${Date.now()}`, { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json() as Promise<{ buildId: string; buildTime: string }>;
      })
      .then((data) => {
        if (cancelled) return;
        if (data.buildId === localBuildId) {
          setLive({ state: 'match', liveBuildId: data.buildId, liveBuildTime: data.buildTime });
        } else {
          setLive({ state: 'mismatch', liveBuildId: data.buildId, liveBuildTime: data.buildTime });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setLive({ state: 'error', message: err?.message ?? 'fetch failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [localBuildId, nonce, liveOrigin]);

  const env = detectEnvironment(host);

  // Auto-refresh prompt: live build is newer than what's loaded in this tab.
  const liveIsNewer =
    live.state === 'mismatch' &&
    new Date(live.liveBuildTime).getTime() > new Date(localBuildTime).getTime();

  useEffect(() => {
    if (!liveIsNewer) setDismissedRefresh(false);
  }, [liveIsNewer]);

  let statusBlock: { icon: JSX.Element; text: string; tone: 'ok' | 'warn' | 'info' };
  if (live.state === 'loading') {
    statusBlock = {
      icon: <Loader2 className="w-4 h-4 animate-spin" aria-hidden />,
      text: 'Checking live build…',
      tone: 'info',
    };
  } else if (live.state === 'match') {
    statusBlock = {
      icon: <CheckCircle2 className="w-4 h-4" aria-hidden />,
      text: `Live matches this build (${formatRelative(live.liveBuildTime)})`,
      tone: 'ok',
    };
  } else if (live.state === 'mismatch') {
    const localT = new Date(localBuildTime).getTime();
    const liveT = new Date(live.liveBuildTime).getTime();
    const direction = localT > liveT ? 'Newer build pending — click Publish → Update' : 'Live is newer than this build — refresh editor';
    statusBlock = {
      icon: <AlertTriangle className="w-4 h-4" aria-hidden />,
      text: `${direction} · live ${formatRelative(live.liveBuildTime)}`,
      tone: 'warn',
    };
  } else {
    statusBlock = {
      icon: <AlertTriangle className="w-4 h-4" aria-hidden />,
      text: `Could not reach live (${live.message})`,
      tone: 'warn',
    };
  }

  return (
    <div
      className={cn(
        'w-full border-b text-xs font-sans',
        statusBlock.tone === 'ok' && 'bg-emerald-950/40 border-emerald-700/40 text-emerald-100',
        statusBlock.tone === 'warn' && 'bg-amber-950/50 border-amber-700/50 text-amber-100',
        statusBlock.tone === 'info' && 'bg-muted/40 border-border text-muted-foreground',
      )}
      role="status"
      aria-live="polite"
    >
      <div className="max-w-7xl mx-auto px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="inline-flex items-center gap-1.5 font-medium">
          <span
            className={cn(
              'inline-block w-2 h-2 rounded-full',
              env.tone === 'live' && 'bg-emerald-400',
              env.tone === 'preview' && 'bg-amber-400',
              env.tone === 'dev' && 'bg-sky-400',
            )}
            aria-hidden
          />
          {env.label}
          {host && <span className="text-foreground/60 font-normal">· {host}</span>}
        </span>

        <span className="inline-flex items-center gap-1.5">
          <span className="opacity-70">Build</span>
          <code className="font-mono">{localBuildId}</code>
          <span className="opacity-70">· {formatRelative(localBuildTime)}</span>
        </span>

        <span className={cn(
          'inline-flex items-center gap-1.5 ml-auto',
          statusBlock.tone === 'ok' && 'text-emerald-200',
          statusBlock.tone === 'warn' && 'text-amber-200',
        )}>
          {statusBlock.icon}
          {statusBlock.text}
        </span>

        <button
          type="button"
          onClick={() => setNonce((n) => n + 1)}
          className="inline-flex items-center gap-1 px-2 py-0.5 border border-current/30 rounded hover:bg-white/5"
          aria-label="Re-check live build"
        >
          <RefreshCw className="w-3 h-3" aria-hidden /> Re-check
        </button>
        <a
          href={`${liveOrigin}/build-info.json`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 px-2 py-0.5 border border-current/30 rounded hover:bg-white/5"
        >
          <ExternalLink className="w-3 h-3" aria-hidden /> Live JSON
        </a>
      </div>

      {liveIsNewer && !dismissedRefresh && (
        <div
          role="alertdialog"
          aria-live="assertive"
          className="border-t border-emerald-700/40 bg-emerald-950/60 text-emerald-50"
        >
          <div className="max-w-7xl mx-auto px-3 py-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1.5 font-medium">
              <RefreshCw className="w-4 h-4" aria-hidden />
              A newer build is live ({live.state === 'mismatch' ? formatRelative(live.liveBuildTime) : ''}). Refresh to load it.
            </span>
            <div className="ml-auto inline-flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                <RefreshCw className="w-3 h-3" aria-hidden /> Refresh now
              </button>
              <button
                type="button"
                onClick={() => setDismissedRefresh(true)}
                className="inline-flex items-center px-2 py-0.5 rounded border border-emerald-300/40 hover:bg-white/5"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBuildStatusBanner;